import { motion } from "framer-motion";
import { CalendarDays, Sparkles, ArrowLeft } from "lucide-react";
import { FESTIVAL_DATES, FESTIVAL_MONTH } from "@/lib/festival";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  return (
    <section id="hero" className="relative pt-28 pb-6 sm:pt-40 sm:pb-32 overflow-hidden" data-testid="hero-section">
      {/* Decorative floating shapes */}
      <FloatingShapes />

      <div className="container-x relative z-10 grid grid-cols-12 gap-4 sm:gap-10 items-center">
        {/* Right side (RTL first) — copy */}
        <div className="col-span-7 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-4 sm:py-2 rounded-full bg-white border border-yellow-300 shadow-sm mb-3 sm:mb-6"
            data-testid="hero-date-badge"
          >
            <CalendarDays className="w-3 h-3 sm:w-4 sm:h-4 text-[#0066CC]" />
            <span className="text-[10px] sm:text-sm font-bold text-[#1E293B]">
              {FESTIVAL_DATES.join(" • ")} {FESTIVAL_MONTH} • 5-8م
            </span>
          </motion.div>

          <h1 className="font-display text-2xl sm:text-6xl lg:text-7xl font-black leading-[1.15] text-[#1E293B]" data-testid="hero-title">
            <KineticWord text="مهرجان" delay={0.05} />{" "}
            <KineticWord text="العودة" delay={0.15} color="#E63946" />{" "}
            <KineticWord text="للمدارس" delay={0.25} />
            <br />
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="inline-block mt-3 relative"
            >
              <span className="kinetic-underline">للأطفال في ليبيا</span>
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-3 sm:mt-6 text-xs sm:text-lg text-[#334155] max-w-2xl leading-relaxed font-medium"
            data-testid="hero-subtitle"
          >
            ثلاثة أيام من الفرح والإبداع والاكتشاف — مسابقات ورشات ومغامرات تنتظر طفلك. سجّل الآن وكن جزءًا من الاحتفال.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-4 sm:mt-8 flex flex-wrap gap-2 sm:gap-4 items-center"
          >
            <button
              onClick={() => scrollTo("register")}
              className="btn-3d group !py-2 !px-4 sm:!py-4 sm:!px-7 text-xs sm:text-base"
              data-testid="hero-cta-register"
            >
              <span>سجّل طفلك الآن</span>
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:-translate-x-1" />
            </button>
            <button
              onClick={() => scrollTo("activities")}
              className="btn-outline !py-2 !px-3 sm:!py-3.5 sm:!px-6 text-xs sm:text-base"
              data-testid="hero-cta-activities"
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>الأنشطة</span>
            </button>
          </motion.div>

          {/* Stat pills — hidden on very small to keep hero compact */}
          <div className="hidden sm:grid mt-12 grid-cols-3 gap-4 max-w-lg" data-testid="hero-stats">
            {[
              { n: "3", l: "أيام من الفرح" },
              { n: "11+", l: "نشاط وورشة" },
              { n: "∞", l: "ذكريات جميلة" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + i * 0.1 }}
                className="playful-card !p-4 text-center"
              >
                <div className="font-display text-3xl font-black text-[#0066CC]">{s.n}</div>
                <div className="text-xs sm:text-sm text-[#475569] mt-1 font-semibold">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Left side — Logo showcase */}
        <div className="col-span-5 lg:col-span-5 flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, type: "spring", stiffness: 90 }}
            className="relative w-full max-w-[140px] sm:max-w-md"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <img
                src="/assets/logo.jpeg"
                alt="مهرجان العودة للمدارس"
                className="w-full h-auto drop-shadow-[0_20px_40px_rgba(0,102,204,0.25)]"
                data-testid="hero-logo"
              />
            </motion.div>
            {/* Orbiting badges - hidden on very small to keep hero compact */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
              className="hidden sm:block absolute inset-0 pointer-events-none"
            >
              <div className="absolute -top-2 right-8 bg-[#FFD600] text-[#1E293B] text-xs font-black px-3 py-1.5 rounded-full shadow-lg">
                جديد ٢٠٢٦
              </div>
              <div className="absolute bottom-12 -left-4 bg-[#E63946] text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg">
                مجاني
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function KineticWord({ text, delay = 0, color = "#1E293B" }) {
  // Animate whole words to preserve Arabic letter joining
  return (
    <motion.span
      initial={{ y: 60, opacity: 0, filter: "blur(6px)" }}
      animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
      transition={{ delay, duration: 0.7, ease: [0.25, 0.9, 0.3, 1] }}
      style={{ color, display: "inline-block" }}
    >
      {text}
    </motion.span>
  );
}

function FloatingShapes() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <motion.div
        className="absolute top-20 right-[8%] w-24 h-24 rounded-full bg-[#FFD600]/30 blur-2xl"
        animate={{ y: [0, 20, 0], x: [0, -10, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-32 left-[10%] w-40 h-40 rounded-full bg-[#0066CC]/20 blur-3xl"
        animate={{ y: [0, -20, 0], x: [0, 15, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-1/3 left-1/3 w-16 h-16 rounded-full bg-[#E63946]/25 blur-2xl"
        animate={{ y: [0, 30, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}
